<template>
  <label
    v-show="!isHidden"
    class="lp-checkbox-wrapper"
    :class="isHidden ? 'lp-checkbox-hidden' : 'lp-checkbox-visible'"
    :aria-label="label"
  >
    <span
      class="lp-checkbox-check-wrapper"
      :class="[
        pulse && !disabled && 'pulse',
        withoutCheckbox && 'hidden p-absolute',
      ]"
    >
      <input
        :id="id"
        ref="checkboxInput"
        type="checkbox"
        v-bind="$attrs"
        class="lp-checkbox-input"
        :checked="modelValue"
        :disabled="disabled"
        :indeterminate="indeterminate"
        @click.stop="toggle"
      />
      <span
        class="lp-checkbox"
        :class="[
          `lp-checkbox-${type}`,
          (indeterminate || modelValue) && `lp-checkbox-${type}-checked`,
          disabled && 'lp-checkbox-disabled',
          disabled && modelValue && 'lp-checkbox-disabled-checked',
        ]"
      >
        <lp-icon v-if="indeterminate" name="minus" color="white" />
        <lp-icon
          v-if="modelValue && !indeterminate"
          :name="check"
          color="white"
        />
      </span>
    </span>
    <span
      class="ly-row lp-checkbox-label-group w-100"
      :class="[withoutCheckbox && 'p-0']"
    >
      <span
        v-if="label && !$slots.default"
        class="lp-checkbox-label w-100"
        :class="[
          lineThrough && modelValue && 'lp-checkbox-label-linethrough',
          disabled && 'lp-checkbox-label-disabled',
          labelClass,
        ]"
      >
        <slot name="pre" />
        <lp-icon
          v-if="icon"
          :name="icon"
          :class="[iconColor && `text-${iconColor}`]"
        />
        {{ label }}
        <template style="display: block" v-html="labelHtml" />
        <lp-icon
          v-if="afterIcon"
          :name="afterIcon"
          :class="[iconColor && `text-${iconColor}`]"
        />
      </span>

      <span
        v-if="subLabel && !$slots.sublabel"
        class="lp-checkbox-label lp-checkbox-label-sub"
        :class="[disabled && 'lp-checkbox-label-disabled']"
      >
        {{ subLabel }}
      </span>

      <span
        v-if="$slots.default"
        class="lp-checkbox-label"
        :class="[
          lineThrough && modelValue && 'lp-checkbox-label-linethrough',
          disabled && 'lp-checkbox-label-disabled',
        ]"
      >
        <slot />
      </span>
      <span
        v-if="$slots.sublabel"
        class="lp-checkbox-label lp-checkbox-label-sub"
        :class="[disabled && 'lp-checkbox-label-disabled']"
      >
        <slot name="sublabel" />
      </span>
      <span
        v-if="$slots.label"
        class="lp-checkbox-label"
        :class="[
          lineThrough && modelValue && 'lp-checkbox-label-linethrough',
          disabled && 'lp-checkbox-label-disabled',
        ]"
      >
        <slot name="label" />
      </span>
    </span>
  </label>
  <transition name="fade-bottom">
    <span
      v-if="message"
      class="lp-input-message mt-half d-flex"
      :class="[
        type && `lp-input-message-${type}`,
        disabled && `lp-input-message-disabled`,
      ]"
    >
      {{ message }}
    </span>
  </transition>
</template>

<script setup lang="ts">
import { computed, inject, Ref, ref, useSlots } from 'vue'
import { Colors } from '@/models/enums'

defineOptions({
  name: 'lp-checkbox',
  inheritAttrs: false,
})

export interface Props {
  type?: Colors
  label?: string | null
  id?: string | number | null
  subLabel?: string | null
  pulse?: boolean
  disabled?: boolean
  indeterminate?: boolean
  lineThrough?: boolean
  check?: string
  icon?: string | null
  iconColor?: string | null
  color?: string | null
  modelValue?: boolean
  pinned?: boolean
  message?: string | null
  labelHtml?: string | HTMLElement | null
  searchIncludesChildren?: boolean
  parent?: string | null
  afterIcon?: string | null
  labelClass?: string | null
  withoutCheckbox?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  type: Colors.PRIMARY,
  label: null,
  id: null,
  subLabel: null,
  pulse: false,
  disabled: false,
  indeterminate: false,
  lineThrough: false,
  check: 'check',
  icon: null,
  iconColor: null,
  color: null,
  modelValue: false,
  pinned: false,
  message: null,
  labelHtml: null,
  searchIncludesChildren: false,
  parent: null,
  afterIcon: null,
  labelClass: null,
  withoutCheckbox: false,
})
const emit = defineEmits({ 'update:modelValue': null })

const slots = useSlots()

const checkboxInput = ref(null)
const pinned = ref(props.pinned)
function toggle() {
  if (props.disabled) {
    return
  }
  const newValue = !props.modelValue
  emit('update:modelValue', newValue)
}

const searchValue = inject<Ref<string | undefined>>(
  'searchEmitValue',
  ref(undefined),
)
const searchFilter = inject<Ref<boolean>>('searchFilter', ref(true))

const isHidden = computed(() => {
  if (pinned.value || !searchFilter.value) {
    return false
  }

  const searchVal = searchValue.value?.toLowerCase() || ''

  if (searchVal === '') {
    return false
  }

  const label =
    (props.label && props.label?.trim()?.length > 0
      ? props.label
      : slots.label
        ? slots.label()?.[0]?.props?.innerHTML
        : ''
    )?.toLowerCase() || ''

  if (props.searchIncludesChildren) {
    const parent = props.parent?.toLowerCase() || ''
    // Show the item if either the parent or the label matches
    return !(parent.includes(searchVal) || label.value.includes(searchVal))
  } else {
    if (typeof label === 'string') {
      return !label.includes(searchVal)
    }
    // Show the item if the label matches
    return !label?.value?.includes(searchVal)
  }
})
</script>
